import { useMutation } from '@tanstack/react-query';
import type {
	ConfirmPaymentData,
	PaymentIntent,
	Stripe,
	StripeElements,
	StripeError,
} from '@stripe/stripe-js';

export interface StripeConfirmPaymentParams {
	/** Client secret from `beginPayment`. Required for deferred Payment Element intents. */
	clientSecret?: string;
	confirmParams?: Partial<ConfirmPaymentData>;
}

/**
 * Confirms a Stripe PaymentIntent with the Payment Element via `stripe.confirmPayment`.
 * Call from a component rendered inside `StripeElementsProvider`.
 *
 * Note - this is wraps a method from Stripe's SDK. This is not a method from the LW core api
 */
const useStripeConfirmPayment = (
	stripe: Stripe | null,
	elements: StripeElements | null,
) => {
	return useMutation<
		PaymentIntent,
		StripeError | Error,
		StripeConfirmPaymentParams
	>({
		mutationFn: async (params) => {
			if (!stripe) {
				throw new Error('stripe is not defined');
			}

			if (!elements) {
				throw new Error('elements is not defined');
			}

			const confirmParams = {
				return_url: window.location.href,
				...params.confirmParams,
			};

			const result = params.clientSecret
				? await stripe.confirmPayment({
						elements,
						clientSecret: params.clientSecret,
						confirmParams,
						redirect: 'if_required',
					})
				: await stripe.confirmPayment({
						elements,
						confirmParams,
						redirect: 'if_required',
					});

			if (result.error) {
				throw result.error;
			}

			return result.paymentIntent;
		},
	});
};

export default useStripeConfirmPayment;
